/**
 * @file 权威状态不变量断言（开发期与测试每步调用；生产按需开启）。
 *
 * 当前覆盖：
 * - **实体守恒**：`牌墙 ∪ 四家手牌 ∪ 四家副露 ∪ 四家弃牌` 恰好 108 个互异 id。
 *   多响（一炮多响）时，除 `ordered[0]` 外的胡牌张为「虚拟牌」，不计入实体守恒
 *   （真实牌实体仍属于点炮张所在的弃牌河），见 GDD 05 §4.3。
 * - `seq` 单调非负；
 * - `winnersInOrder` 长度 ≤ 3 且与各家 `hasWon` 一致。
 *
 * 不变量被破坏 ⇒ 抛 {@link InvariantViolationError}（意味着引擎有 bug，不应被吞）。
 */

import type { GameState } from './types';
import { InvariantViolationError } from './errors';

export function assertStateInvariants(state: GameState): void {
  const ids = new Set<number>();
  const add = (id: number): void => {
    if (!Number.isInteger(id) || id < 0 || id >= 108) {
      throw new InvariantViolationError(`非法 TileId: ${id}`, { id });
    }
    if (ids.has(id)) {
      throw new InvariantViolationError(`TileId 重复出现: ${id}`, { id });
    }
    ids.add(id);
  };

  for (const id of state.wall) add(id);
  for (const p of state.players) {
    for (const id of p.hand) add(id);
    for (const m of p.melds) for (const id of m.tiles) add(id);
    for (const id of p.discards) add(id);
  }

  if (ids.size !== 108) {
    throw new InvariantViolationError(`实体守恒失败：实际 ${ids.size} 张，应为 108`, { count: ids.size });
  }

  if (state.seq < 0) {
    throw new InvariantViolationError('seq 非负', { seq: state.seq });
  }

  if (state.winnersInOrder.length > 3) {
    throw new InvariantViolationError('winnersInOrder 长度不得超过 3', { len: state.winnersInOrder.length });
  }
  for (const seat of state.winnersInOrder) {
    if (!state.players[seat]!.hasWon) {
      throw new InvariantViolationError(`winnersInOrder 含未标记 hasWon 的座位: ${seat}`, { seat });
    }
  }
  for (let seat = 0; seat < 4; seat++) {
    if (state.players[seat]!.hasWon && !state.winnersInOrder.includes(seat as 0 | 1 | 2 | 3)) {
      throw new InvariantViolationError(`座位 ${seat} 已 hasWon 但未出现在 winnersInOrder`, { seat });
    }
  }
}
